import { Link } from "@tanstack/react-router";
import { Plus, Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { FilterBar, FilterChip } from "@/components/cinch/FilterBar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth/useCan";
import { cn } from "@/lib/utils";

import {
  useObjectives,
  useQuarters,
  useQuarterSummary,
  useUsers,
  type Objective,
} from "../api";
import { ActionItemsPanel } from "../components/ActionItemsPanel";
import { ObjectiveCard } from "../components/ObjectiveCard";
import { ObjectiveForm } from "../components/ObjectiveForm";
import { STATUS_META, STATUS_VARIANT, VARIANT_BG_CLASS, ownerColor } from "../constants";

function pickDefaultQuarter(quarters: { id: string; start_date: string; end_date: string; is_closed: boolean }[]): string | undefined {
  const first = quarters[0];
  if (!first) return undefined;
  const today = new Date().toISOString().slice(0, 10);
  const spanning = quarters.find((q) => q.start_date <= today && today <= q.end_date);
  if (spanning) return spanning.id;
  const open = quarters.find((q) => !q.is_closed);
  return (open ?? first).id;
}

export function ObjectivesPage() {
  const canWrite = useCan("objectives.write");
  const canAdmin = useCan("objectives.admin");
  const quarters = useQuarters();
  const users = useUsers();

  const [quarterId, setQuarterId] = useState<string | undefined>(undefined);
  const [ownerFilter, setOwnerFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Objective | null>(null);

  // Default to the current quarter once quarters load.
  useEffect(() => {
    if (!quarterId && quarters.data) {
      setQuarterId(pickDefaultQuarter(quarters.data));
    }
  }, [quarterId, quarters.data]);

  const summary = useQuarterSummary(quarterId);
  const objectives = useObjectives({ quarterId, ownerId: ownerFilter, status: statusFilter });

  const grouped = useMemo(() => groupByOwner(objectives.data ?? []), [objectives.data]);

  if (quarters.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (quarters.data && quarters.data.length === 0) {
    return (
      <EmptyQuarters canAdmin={canAdmin} />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-brand">
            Executive Priorities
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Objectives</h1>
          <p className="text-sm text-muted-foreground">
            {summary.data
              ? `${summary.data.objective_count} objectives · ${summary.data.completed_count} complete`
              : "Quarterly priorities for the leadership team"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {summary.data && <QuarterProgress pct={summary.data.progress_pct} />}
          {canAdmin && (
            <Button asChild variant="outline" size="sm" className="gap-1">
              <Link to="/objectives/admin">
                <Settings className="h-3.5 w-3.5" /> Quarters
              </Link>
            </Button>
          )}
          {canWrite && quarterId && (
            <Button type="button" className="gap-1" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New objective
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <FilterBar className="rounded-lg border border-border bg-card px-4 py-3">
        <Select value={quarterId ?? ""} onValueChange={setQuarterId}>
          <SelectTrigger className="h-8 w-[180px] rounded-full text-xs font-semibold">
            <SelectValue placeholder="Pick a quarter" />
          </SelectTrigger>
          <SelectContent>
            {quarters.data?.map((q) => (
              <SelectItem key={q.id} value={q.id}>
                {q.label}
                {q.is_closed ? " (closed)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FilterChip
          label="Owner"
          valueLabel={users.data?.find((u) => u.id === ownerFilter)?.display_name}
        >
          <FilterOptionList>
            <FilterOption selected={!ownerFilter} onClick={() => setOwnerFilter(undefined)}>
              All owners
            </FilterOption>
            {users.data?.map((u) => (
              <FilterOption
                key={u.id}
                selected={ownerFilter === u.id}
                onClick={() => setOwnerFilter(u.id)}
                dotStyle={{ background: ownerColor(u.id) }}
              >
                {u.display_name}
              </FilterOption>
            ))}
          </FilterOptionList>
        </FilterChip>

        <FilterChip
          label="Status"
          valueLabel={STATUS_META.find((s) => s.key === statusFilter)?.label}
        >
          <FilterOptionList>
            <FilterOption selected={!statusFilter} onClick={() => setStatusFilter(undefined)}>
              All statuses
            </FilterOption>
            {STATUS_META.map((s) => (
              <FilterOption
                key={s.key}
                selected={statusFilter === s.key}
                onClick={() => setStatusFilter(s.key)}
                dotClass={VARIANT_BG_CLASS[STATUS_VARIANT[s.key]]}
              >
                {s.label}
              </FilterOption>
            ))}
          </FilterOptionList>
        </FilterChip>
      </FilterBar>

      {/* Body: objectives + action items */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-8">
          {objectives.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {objectives.data && objectives.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No objectives match these filters.
            </p>
          )}
          {grouped.map(([ownerName, items]) => (
            <section key={ownerName} className="flex flex-col gap-3">
              <div className="flex items-center gap-3 border-b border-border pb-2">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white"
                  style={{ background: ownerColor(items[0]?.owner_id) }}
                >
                  {ownerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold">{ownerName}</div>
                  <div className="text-xs text-muted-foreground">
                    {items.length} priorit{items.length === 1 ? "y" : "ies"} ·{" "}
                    {items.filter((i) => i.status === "complete").length} complete
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2.5">
                {items.map((obj) => (
                  <ObjectiveCard
                    key={obj.id}
                    objective={obj}
                    canWrite={canWrite}
                    onEdit={() => setEditing(obj)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        {quarterId && <ActionItemsPanel quarterId={quarterId} />}
      </div>

      {creating && quarterId && (
        <ObjectiveForm quarterId={quarterId} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <ObjectiveForm
          quarterId={editing.quarter_id}
          objective={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function groupByOwner(objectives: Objective[]): [string, Objective[]][] {
  const groups = new Map<string, Objective[]>();
  for (const obj of objectives) {
    const name = obj.owner_name ?? "Unassigned";
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name)!.push(obj);
  }
  return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}

function QuarterProgress({ pct }: { pct: number }) {
  return (
    <div className="min-w-[200px] rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Quarter progress
      </div>
      <div className="text-3xl font-bold leading-none">
        {Math.round(pct)}
        <span className="text-lg text-muted-foreground">%</span>
      </div>
      <Progress value={Math.min(100, Math.max(0, pct))} className="mt-2 h-1.5" />
    </div>
  );
}

function FilterOptionList({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-0.5">{children}</div>;
}

function FilterOption({
  selected,
  onClick,
  dotClass,
  dotStyle,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  dotClass?: string;
  dotStyle?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
        selected && "bg-brand-light text-brand",
      )}
    >
      {(dotClass || dotStyle) && (
        <span className={cn("h-2.5 w-2.5 rounded-full", dotClass)} style={dotStyle} />
      )}
      {children}
    </button>
  );
}

function EmptyQuarters({ canAdmin }: { canAdmin: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 py-20 text-center">
      <h1 className="text-2xl font-bold">No quarters yet</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Objectives are organized by quarter. Create the first quarter to get started.
      </p>
      {canAdmin && (
        <Button asChild className="gap-1">
          <Link to="/objectives/admin">
            <Settings className="h-4 w-4" /> Manage quarters
          </Link>
        </Button>
      )}
    </div>
  );
}
